import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';
import WebFontLoader from 'webfontloader';

import type { Service } from 'webapp-types';
import { AppEnv, PopupCloseReason } from 'webapp/enums/enums';
import { isEditableElementActive } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import CommonPopupRequestDiagnosticsComponent from 'webapp/components/common/popup-request-diagnostics';
import type DesignerBoardBlockController from 'webapp/controllers/designer/board/block';

import type PopupModal from 'webapp/models-custom/popup/modal';

import { Severity } from 'webapp/services/logger';

type Args = object;

export default class AppLoadedEventsComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('logger') declare loggerService: Service.Logger;

	// Manually flip to `true` while developing with `watch` in places where dev-only backtracking
	// assertions ("attempted to update X, but it had already been used previously") crash the app.
	// This wraps certain updates in `next()` (see checkToApplyWatchHack in utils/common), which MIGHT
	// make the app behave differently — if you touch affected code while this is on, you MUST verify
	// against a production build before shipping. Committing this as `true` is blocked by lefthook.
	preferWatchHackMode = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// Only ever activates on a local dev server — `window.env` is set server-side, so a deployed
		// (develop/production) build can never report `local`, keeping prod fully intact.
		if (this.preferWatchHackMode && window.env === AppEnv.LOCAL) {
			window.isWatchHackMode = true;
		}

		// Will trigger special event when previewdata font is loaded,
		// because then we need to reposition / recalculate graphs etc items (as they are pixel-dependant)
		try {
			WebFontLoader.load({
				google: {
					families: [
						'Roboto:italic,300,400,500,600,700:cyrillic,cyrillic-ext,greek,greek-ext,latin-ext,vietnamese',
						'Roboto+Mono:400:latin-ext',
					],
				},
				active: () => {
					this.navigationService.isFontLoaded = true;
				},
				inactive: () => {
					// this.logger.logError('Webapp: Failed to load font', null, Severity.INFO);
					this.navigationService.isFontLoaded = true;
				},
			});
			WebFontLoader.load({
				google: {
					families: ['Roboto'],
					text: '∞',
				},
			});
		} catch (e) {
			this.loggerService.logError(
				'Webapp: WebFont error',
				e as object | null,
				Severity.CRITICAL,
			);
			this.navigationService.isFontLoaded = true;
		}
	}

	@action
	setupView() {
		this.bindDebugPopupOpenEvents();
		this.bindClosePopupOnEscEvent();
	}

	bindDebugPopupOpenEvents() {
		// Open debugging popup with info about current user
		// CTRL+ALT+b

		$(window).on('keydown.debugging', (e) => {
			// TODO: allow public datawalls to open this when have more useful info
			const allowKeypress =
				!this.requestService.wallToken &&
				(this.currentUserService.loggedInAsPowerUser ||
					this.currentUserService.isPowerUser ||
					this.currentUserService.isPlatformAdmin ||
					this.currentUserService.hasDataboxEmail);

			if (
				allowKeypress &&
				e.ctrlKey &&
				e.altKey &&
				e.keyCode === 66 &&
				!isEditableElementActive()
			) {
				const controller = this.navigationService.appRoute.controllerFor(
						'designer.board.block',
					) as DesignerBoardBlockController,
					block = controller?.boardState?.selectedBlock;
				if (block) {
					this.mmBlocksService.openDebugPopup(block.numericId!);
				} else {
					this.navigationService.openDebugPopup();
				}
			}
		});

		// open request diagnostics popup with info about last 10 requests
		// CTRL+ALT+d
		$(window).on('keydown.request-diagnostics', (e) => {
			const allowKeypress =
				this.currentUserService.loggedInAsPowerUser ||
				this.currentUserService.isPowerUser ||
				this.currentUserService.isPlatformAdmin ||
				this.currentUserService.hasDataboxEmail;

			if (
				allowKeypress &&
				e.ctrlKey &&
				e.altKey &&
				e.keyCode === 68 &&
				!isEditableElementActive()
			) {
				const popup = this.popupService.createModal(
					CommonPopupRequestDiagnosticsComponent,
					{
						popupClass: 'popup-type-request-diagnostics',
					},
				);

				popup.open(); // <void>
			}
		});
	}

	bindClosePopupOnEscEvent() {
		// close modal on esc (unless a dropdown is open, then close that)
		const bodyCt = $('body');
		bodyCt
			.on('keyup.closeOnEsc', this.closePopupOnEsc)
			.on('select2:open', function () {
				bodyCt.off('keyup.closeOnEsc');
			})
			.on('select2:close', () => {
				// bind esc only if no other dropdowns are open
				// when opening new dropdown when previous isn't closed the events go in order open (new) -> close (old)
				// thats why the timeout and checking for other open dropdowns
				later(() => {
					if ($('.select2-container--open').length === 0) {
						bodyCt.on('keyup.closeOnEsc', this.closePopupOnEsc);
					}
				}, 400); // depends how long user likes to hold the esc key...
			});
	}

	@action
	closePopupOnEsc(e: Event) {
		if ((e as KeyboardEvent).key === 'Escape') {
			// ESC
			const activePopup = this.popupService.activePopup as PopupModal;
			const isPeriodPickerMaximized = $('.period_picker_maximize.visible').length > 0; // Asana task: https://app.asana.com/0/1209768184402601/1209666194655087
			const isDrawer = !!activePopup?.isDrawer;
			if (
				activePopup &&
				!isPeriodPickerMaximized &&
				((activePopup.showClose && activePopup.closeOnOverlayClick) || isDrawer)
			) {
				void activePopup.close(PopupCloseReason.ESCKEY);
			}
		}
	}

	<template>
		<div {{didInsert this.setupView}}></div>
	</template>
}
